import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, X, CheckCircle, ArrowUp, 
  ArrowDown, AlertTriangle, Clock, Check,
  ArrowRight, RefreshCw, Shield, Mail
} from 'lucide-react';

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: { 
      type: "spring",
      duration: 0.5,
      bounce: 0.3
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95, 
    y: 10,
    transition: { 
      duration: 0.2,
      ease: "easeOut"
    }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

/**
 * Modal de confirmación para cambio de plan
 * 
 * @param {Object} props
 * @param {Boolean} props.isOpen - Controla si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {Object} props.simulacion - Datos de la simulación del cambio
 * @param {Function} props.onConfirm - Función que se ejecuta al confirmar el cambio
 * @param {Function} props.onSuccess - Función que se ejecuta cuando el cambio es exitoso
 */
const PlanConfirmation = ({ isOpen, onClose, simulacion, onConfirm, onSuccess, precioFinal = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [success, setSuccess] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const inputRef = useRef(null);

  // Formateo de fechas
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Reset del estado cuando cambia isOpen
  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setError(null);
      setVerificationRequired(false);
      setVerificationCode('');
      setSuccess(false);
      setShowAdditionalInfo(false);
    }
  }, [isOpen]);

  // Focus en el input de código cuando se requiere verificación
  useEffect(() => {
    if (verificationRequired && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
  }, [verificationRequired]);

  // Manejador para confirmar el cambio
  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isFreePlan = precioFinal <= 0;
      const planId = simulacion?.plan_nuevo_id;
      
      console.log('Intentando confirmar cambio con datos:', {
        planId,
        ciclo: simulacion?.ciclo_nuevo,
        verificationCode: verificationCode || null,
        isFreePlan
      });
      
      if (!simulacion?.ciclo_nuevo) {
        throw new Error('Información de ciclo no disponible');
      }
      
      const result = await onConfirm(verificationCode, isFreePlan);
      
      console.log('Resultado de confirmación:', result);
      
      if (result.verificacion_requerida) {
        setVerificationRequired(true);
        setLoading(false);
      } else if (result.success) {
        setSuccess(true);
        setLoading(false);
        
        // Ejecutar callback de éxito después de un delay
        setTimeout(() => {
          onSuccess(result);
        }, 2000);
      }
    } catch (err) {
      console.error('Error detallado al confirmar cambio:', err);
      setLoading(false);
      setError(err.response?.data?.error || err.message || 'Error al confirmar el cambio');
    }
  };

  // Manejar input de código de verificación
  const handleCodeChange = (e) => {
    // Solo permitir caracteres numéricos y limitar a 6 dígitos
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  // Si no hay simulación o no está abierto, no renderizar
  if (!isOpen || !simulacion) return null;

  // Determinar si es upgrade o downgrade
  const isUpgrade = simulacion.es_upgrade;
  const isDowngrade = simulacion.es_downgrade;
  const isCycleChange = simulacion.es_cambio_ciclo;
  const isScheduledChange = simulacion.cambio_programado;

  // Renderizado condicional basado en el estado
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
          {/* Overlay */}
          <motion.div 
            className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div 
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-6 py-4 ${success ? 'bg-green-600' : isUpgrade ? 'bg-blue-600' : isDowngrade ? 'bg-amber-600' : 'bg-indigo-600'} text-white`}>
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold flex items-center">
                  {success ? (
                    <>
                      <CheckCircle className="w-6 h-6 mr-2" />
                      ¡Cambio Confirmado!
                    </>
                  ) : verificationRequired ? (
                    <>
                      <Shield className="w-6 h-6 mr-2" />
                      Verificación Requerida
                    </>
                  ) : isScheduledChange ? (
                    <>
                      <Clock className="w-6 h-6 mr-2" />
                      Cambio Programado
                    </>
                  ) : (
                    <>
                      <Calendar className="w-6 h-6 mr-2" />
                      Confirmar Cambio de Plan
                    </>
                  )}
                </h3>
                <button 
                  onClick={onClose}
                  className="rounded-full p-1 hover:bg-white hover:bg-opacity-20 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Body */}
            <div className="px-6 pt-4 pb-6">
              {success ? (
                <div className="py-8 text-center">
                  <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-800 mb-2">
                    {isScheduledChange 
                      ? 'Cambio programado exitosamente' 
                      : 'Plan actualizado exitosamente'}
                  </h4>
                  <p className="text-gray-600">
                    {isScheduledChange
                      ? `Tu plan cambiará automáticamente el ${formatDate(simulacion.fecha_efectiva)}.`
                      : 'Tu plan ha sido actualizado y ya puedes disfrutar de sus beneficios.'}
                  </p>
                </div>
              ) : verificationRequired ? (
                <div className="py-4">
                  <div className="flex items-center justify-center mb-4">
                    <Mail className="w-12 h-12 text-blue-600" />
                  </div>
                  <p className="text-gray-700 mb-4 text-center">
                    Hemos enviado un código de verificación a tu correo electrónico para confirmar este cambio de plan.
                  </p>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código de verificación
                    </label>
                    <input 
                      ref={inputRef}
                      type="text" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="Ingresa el código de 6 dígitos"
                      value={verificationCode}
                      onChange={handleCodeChange}
                      maxLength={6}
                    />
                  </div>
                  
                  {error && (
                    <div className="mb-4 text-sm text-red-600 bg-red-50 p-2 rounded-lg flex items-start">
                      <AlertTriangle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                      <p>{error}</p>
                    </div>
                  )}
                  
                  <button
                    onClick={handleConfirm}
                    disabled={loading || verificationCode.length !== 6}
                    className={`w-full py-2 px-4 rounded-lg text-white font-medium 
                      ${loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : verificationCode.length === 6 
                          ? 'bg-blue-600 hover:bg-blue-700' 
                          : 'bg-gray-400 cursor-not-allowed'
                      } transition-colors`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Verificando...
                      </span>
                    ) : 'Verificar y Completar'}
                  </button>
                </div>
              ) : (
                <>
                  {/* Información de cambio de plan */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-lg font-medium text-gray-800">
                          {simulacion.plan_actual || 'Sin plan'}
                        </h4>
                        <p className="text-sm text-gray-500">Plan actual</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                      <div className="text-right">
                        <h4 className="text-lg font-medium text-gray-800">
                          {simulacion.plan_nuevo}
                        </h4>
                        <p className="text-sm text-gray-500">Plan nuevo</p>
                      </div>
                    </div>
                    
                    {/* Indicador de tipo de cambio */}
                    <div className={`flex items-center p-3 rounded-lg ${
                      isUpgrade 
                        ? 'bg-blue-50 text-blue-700' 
                        : isDowngrade 
                          ? 'bg-amber-50 text-amber-700' 
                          : 'bg-indigo-50 text-indigo-700'
                    }`}>
                      {isUpgrade ? (
                        <>
                          <ArrowUp className="w-5 h-5 mr-2" />
                          <p className="text-sm font-medium">
                            Estás haciendo un upgrade de plan
                          </p>
                        </>
                      ) : isDowngrade ? (
                        <>
                          <ArrowDown className="w-5 h-5 mr-2" />
                          <p className="text-sm font-medium">
                            Estás haciendo un downgrade de plan
                          </p>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2" />
                          <p className="text-sm font-medium">
                            Estás cambiando el ciclo de facturación
                          </p>
                        </>
                      )}
                    </div>
                    
                    {/* Información del cambio */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <h5 className="font-medium text-gray-700">Detalles del cambio</h5>
                      </div>
                      <div className="p-4 space-y-3">
                        {simulacion.precio_actual !== undefined && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Precio actual:</span>
                            <span className="font-medium text-gray-800">
                              S/ {parseFloat(simulacion.precio_actual).toFixed(2)} / {simulacion.ciclo_actual}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Nuevo precio:</span>
                          <span className="font-medium text-gray-800">
                            S/ {parseFloat(simulacion.precio_nuevo).toFixed(2)} / {simulacion.ciclo_nuevo}
                          </span>
                        </div>
                        
                        {isScheduledChange ? (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Fecha de cambio:</span>
                            <span className="font-medium text-gray-800">
                              {formatDate(simulacion.fecha_efectiva)}
                            </span>
                          </div>
                        ) : (
                          <>
                            {simulacion.dias_restantes > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Días restantes actuales:</span>
                                <span className="font-medium text-gray-800">
                                  {simulacion.dias_restantes} días
                                </span>
                              </div>
                            )}
                            
                            {simulacion.saldo_prorrateado > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Saldo prorrateado:</span>
                                <span className="font-medium text-gray-800">
                                  S/ {parseFloat(simulacion.saldo_prorrateado).toFixed(2)}
                                </span>
                              </div>
                            )}
                            
                            {simulacion.dias_equivalentes > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Días equivalentes en nuevo plan:</span>
                                <span className="font-medium text-gray-800">
                                  {simulacion.dias_equivalentes} días
                                </span>
                              </div>
                            )}
                          </>
                        )}
                        
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Nueva fecha de vencimiento:</span>
                          <span className="font-medium text-gray-800">
                            {formatDate(simulacion.fecha_nueva_fin)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Botón para mostrar/ocultar información adicional */}
                    <button
                      onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
                      className="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center"
                    >
                      {showAdditionalInfo ? 'Ocultar detalles' : 'Ver más detalles'}
                    </button>
                    
                    {/* Información adicional */}
                    {showAdditionalInfo && (
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                        <p className="mb-2">
                          <strong>¿Cómo funciona el prorrateado?</strong>
                        </p>
                        {isUpgrade && (
                          <p>
                            Al cambiar a un plan con mayor precio, tu periodo de suscripción se acorta proporcionalmente.
                            El valor restante de tu plan actual se aplica al nuevo plan.
                          </p>
                        )}
                        {isDowngrade && (
                          <p>
                            Al cambiar a un plan con menor precio, tu periodo de suscripción se extiende proporcionalmente.
                            El valor restante de tu plan actual se aplica al nuevo plan.
                          </p>
                        )}
                        {isCycleChange && (
                          <p>
                            {isScheduledChange 
                              ? 'El cambio de ciclo mensual a anual se aplicará al final de tu ciclo actual para aprovechar al máximo tu plan actual.' 
                              : 'El cambio de ciclo anual a mensual se aplica inmediatamente, prorrateando el saldo restante.'}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Mensaje de error si existe */}
                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg flex items-start">
                        <AlertTriangle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                        <p>{error}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Acciones */}
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={onClose}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={loading}
                      className={`flex-1 py-2 px-4 rounded-lg text-white font-medium 
                        ${loading 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : isUpgrade 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : isDowngrade 
                              ? 'bg-amber-600 hover:bg-amber-700' 
                              : 'bg-indigo-600 hover:bg-indigo-700'
                        } transition-colors`}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center">
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Procesando...
                        </span>
                      ) : isScheduledChange ? 'Programar Cambio' : 'Confirmar Cambio'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PlanConfirmation;